/**
 * The merchant application frame: fixed rail, sticky topbar, scrolling body.
 *
 * Merchant-only. The Super Admin console has its own `SuperAdminLayout`; what
 * the two have in common lives in `shellParts`.
 */

import { type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.js'
import { useRealtimeStatus } from '../hooks/useRealtime.js'
import { useAlerts } from '../hooks/useAlerts.js'
import { api } from '../lib/api.js'
import { PLANS, type BillingSummary, type PlanKey } from '@outfalak/shared'
import { dateOnly, money, PLATFORM_LABEL } from '../lib/format.js'
import { isEmbedded } from '../lib/shopify.js'
import { Menu, MenuItem } from './ui.js'
import { IconByName } from './iconByName.js'
import { AccountMenu, Sidebar, TopbarSearch, useRail } from './shellParts.js'
import { merchantNav, type NavSection } from './nav.js'
import { ArrowRightIcon, BellIcon, ChartIcon, MenuIcon, SparkIcon, StoreIcon, WalletIcon } from './icons.js'

const STATUS_LABEL = {
  idle: 'Offline',
  connecting: 'Connecting…',
  live: 'Live',
  reconnecting: 'Reconnecting…',
  unauthorized: 'Signed out',
} as const

export function AppShell({ children }: { children: ReactNode }): ReactNode {
  const rail = useRail()
  const sections = merchantNav

  return (
    <div className={['shell', ...rail.classes].join(' ')}>
      {/* Shopify's embedded admin bills through Shopify only (requirement 1.2.1),
          so a wallet-plan upgrade prompt has no business appearing there. */}
      <Sidebar sections={sections} homeTo="/">
        {!isEmbedded() && <PlanCard />}
      </Sidebar>
      <button type="button" className="sidebar__scrim" aria-label="Close navigation" onClick={rail.close} />

      <div className="shell__main">
        <Topbar sections={sections} onToggleRail={rail.toggle} />
        <main className="shell__body">{children}</main>
      </div>
    </div>
  )
}

/*
 * The merchant's current plan, and the way up from it.
 *
 * Reads the same `['billing']` query as the Billing page, so switching plan
 * there updates this card without a second fetch. Nothing renders until the
 * plan is known — a card that says "Starter" while loading would tell a paying
 * merchant they had been downgraded.
 */
function PlanCard(): ReactNode {
  const { data } = useQuery({ queryKey: ['billing'], queryFn: () => api<BillingSummary>('/v1/billing') })
  if (!data) return null

  const current = data.plan
  const keys = Object.keys(PLANS) as PlanKey[]
  const next = keys.slice(keys.indexOf(current.key) + 1)[0]
  const price = Number(current.price)

  return (
    <div className="sidebar__foot">
      <div className="upsell">
        <p className="upsell__eyebrow">Current plan</p>
        <div className="upsell__title">
          <SparkIcon size={16} />
          {current.name}
          <span className="upsell__price">{price > 0 ? `${money(price)}/mo` : 'Free'}</span>
        </div>
        {data.renewsAt && <p>Renews {dateOnly(data.renewsAt)}</p>}
        {next ? (
          <NavLink to="/wallet/plans" className="btn btn--primary btn--sm btn--block">
            Upgrade to {PLANS[next].name}
            <ArrowRightIcon size={15} />
          </NavLink>
        ) : (
          <NavLink to="/wallet/plans" className="btn btn--sm btn--block">
            Manage plan
          </NavLink>
        )}
      </div>
    </div>
  )
}

// ── Topbar ──────────────────────────────────────────────────────────────────

function Topbar({ sections, onToggleRail }: { sections: NavSection[]; onToggleRail: () => void }): ReactNode {
  const { user, shop, shops, switchShop } = useAuth()
  const status = useRealtimeStatus()
  const navigate = useNavigate()

  const displayName = user?.name ?? shop?.name ?? shop?.domain ?? 'Account'
  const role = user?.merchantId ? 'Owner' : 'Merchant'

  return (
    <header className="topbar">
      <button type="button" className="icon-btn" onClick={onToggleRail} aria-label="Toggle navigation">
        <MenuIcon size={20} />
      </button>

      {/* Embedded, the app is bound to the one Shopify store whose admin it is
          inside — there is nothing to switch to, and "Add a store" leads off
          Shopify. */}
      {shops.length > 0 && (
        <Menu
          bordered
          align="left"
          label="Switch store"
          trigger={
            <>
              <StoreIcon size={16} style={{ color: 'var(--ink-soft)' }} />
              <span style={{ fontWeight: 550, fontSize: '.88rem' }}>{shop?.name ?? shop?.domain ?? 'All Stores'}</span>
            </>
          }
        >
          {(close) => (
            <>
              <div className="menu__head">
                <strong style={{ fontSize: '.8rem' }}>Your stores</strong>
                <small className="faint" style={{ fontSize: '.75rem' }}>
                  {shops.length} connected
                </small>
              </div>
              {shops.map((option) => (
                <MenuItem
                  key={option.id}
                  active={option.id === shop?.id}
                  icon={<StoreIcon size={16} />}
                  onClick={() => {
                    void switchShop(option.id)
                    close()
                  }}
                >
                  {option.name ?? option.domain}
                  <small>{PLATFORM_LABEL[option.platform] ?? option.platform}</small>
                </MenuItem>
              ))}
              <div className="menu__sep" />
              <MenuItem
                icon={<StoreIcon size={16} />}
                onClick={() => {
                  navigate('/integrations')
                  close()
                }}
              >
                Add a store
              </MenuItem>
            </>
          )}
        </Menu>
      )}

      <TopbarSearch sections={sections} placeholder="Search anything…" />

      <div className="topbar__right">
        {/*
          The connection state is shown rather than hidden. A dashboard whose
          numbers have quietly stopped updating looks exactly like a quiet
          afternoon, and a merchant should never have to guess which it is.
        */}
        <span className={`pill pill--${status}`} title={`Realtime: ${STATUS_LABEL[status]}`}>
          <span className="pill__dot" aria-hidden="true" />
          {STATUS_LABEL[status]}
        </span>

        <NotificationsMenu />

        <WalletPill />

        <span className="topbar__divider" />

        <AccountMenu
          name={displayName}
          email={user?.email ?? shop?.domain ?? ''}
          role={role}
          profileTo="/settings/profile"
          settingsTo="/settings"
        />
      </div>
    </header>
  )
}

// ── Notifications ───────────────────────────────────────────────────────────

/**
 * The bell, reading `/v1/alerts`.
 *
 * This was three hard-coded rows for a long time — a payment, a recovered
 * checkout and a low balance — which looked exactly like a working feature and
 * was the reason nobody noticed there wasn't one. Everything below now comes
 * from the store's own orders, refunds, top-ups and audit trail; where the
 * platform records nothing, the menu says so rather than filling the gap.
 *
 * Read state is marked on the way *out*, not on open. Clearing the badge the
 * instant the panel appears takes the "new" marks off the rows while the
 * merchant is still reading them, and those marks are the only thing telling
 * them where to stop.
 */
/**
 * The merchant's wallet balance, one glance in the header.
 *
 * Reads the same `/v1/wallet` query the Wallet page does, so the pill and the
 * page never disagree. A negative balance wears the bad tone; the title names
 * the overdraft floor the API enforces, so a refused debit is never a mystery.
 * Merchant chrome only — the admin console has no single wallet to show.
 */
function WalletPill(): ReactNode {
  const { user, shop } = useAuth()

  const summary = useQuery({
    queryKey: ['wallet'],
    queryFn: () => api<{ balance: string; currency: string; minBalance?: string }>('/v1/wallet'),
    enabled: Boolean(user ?? shop),
    staleTime: 60_000,
  })

  if (summary.isPending || summary.isError || !summary.data) return null

  const { balance, currency, minBalance } = summary.data
  const negative = Number(balance) < 0

  return (
    <NavLink
      to="/wallet"
      className={['pill', 'wallet-pill', negative ? 'is-negative' : ''].filter(Boolean).join(' ')}
      title={minBalance ? `Wallet balance. Overdraft to ${money(minBalance, currency)}.` : 'Wallet balance.'}
    >
      <WalletIcon size={15} />
      {money(balance, currency)}
    </NavLink>
  )
}

// ── Notifications ─────────────────────────────────────────────────────────────────
function NotificationsMenu(): ReactNode {
  const navigate = useNavigate()
  const { feed, loading, error, shopId, markRead } = useAlerts()

  const unread = feed?.unread ?? 0
  const items = feed?.items ?? []

  return (
    <Menu
      wide
      label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      onOpenChange={(open) => {
        if (!open) markRead()
      }}
      trigger={
        <span style={{ position: 'relative', display: 'grid', placeItems: 'center' }}>
          <BellIcon size={19} />
          {unread > 0 && (
            <span className="icon-btn__count" style={{ top: -4, right: -6 }}>
              {/* Past this the number stops being readable at 11px and starts
                  being a shape. The list underneath is the honest count. */}
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </span>
      }
    >
      {(close) => (
        <>
          <div className="menu__head" style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: '.8rem' }}>Notifications</strong>
            <span className="spacer" />
            {unread > 0 && (
              <small className="faint" style={{ fontSize: '.72rem' }}>
                {unread} unread
              </small>
            )}
          </div>

          {!shopId && <div className="menu__empty">Choose a store to see what has happened in it.</div>}

          {shopId && loading && <div className="menu__empty">Loading…</div>}

          {shopId && error && <div className="menu__empty">{error}</div>}

          {shopId && !loading && !error && items.length === 0 && (
            <div className="menu__empty">
              Nothing to report.
              <br />
              Payments, refunds and wallet alerts land here.
            </div>
          )}

          {items.length > 0 && (
            <div className="menu__list">
              {items.map((alert) => (
                <button
                  key={alert.id}
                  type="button"
                  role="menuitem"
                  className={`menu__item alert${alert.unread ? '' : ' alert--read'}`}
                  onClick={() => {
                    if (alert.to) navigate(alert.to)
                    close()
                  }}
                >
                  <span className="alert__icon" style={{ color: alert.tone }}>
                    <IconByName name={alert.icon} size={16} />
                  </span>
                  <span className="spacer" style={{ textAlign: 'left' }}>
                    <span className="alert__title">{alert.title}</span>
                    <small>{alert.meta}</small>
                  </span>
                  <span className="alert__ago">{alert.ago}</span>
                  {/* role="img" rather than a bare span: an unlabelled dot is
                      invisible to a screen reader, and it is the only thing on
                      the row saying this one is new. */}
                  {alert.unread && <span className="alert__new" role="img" aria-label="Unread" />}
                </button>
              ))}
            </div>
          )}

          {items.length > 0 && (
            <div className="menu__foot">
              {/* The dashboard's activity panel is the longer version of this
                  list, and it is the only screen that shows more of it. */}
              <MenuItem
                icon={<ChartIcon size={16} />}
                onClick={() => {
                  navigate('/')
                  close()
                }}
              >
                Open the activity feed
              </MenuItem>
            </div>
          )}
        </>
      )}
    </Menu>
  )
}
